import { Component, DestroyRef, ElementRef, inject, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import * as L from 'leaflet';
import { Marker } from 'leaflet';
import { DeviceProfileService } from '../../services/device-profile.service';
import { DeviceService } from '../../services/device.service';
import { MapIconService } from '../../services/map-icon.service';
import { PageLoaderService } from '../../services/page-loader.service';
import { DialogBase } from '../../shared/components/dialogs/dialog-base/dialog-base';
import { InputSelectButton } from '../../shared/components/input-select-button/input-select-button';
import { InputSelectComponent } from '../../shared/components/input-select/input-select';
import { InputTextComponent } from '../../shared/components/input-text/input-text.component';
import { AbstractMap } from '../../shared/components/map/map';
import { TextLabel } from '../../shared/components/text-label/text-label';
import { TranslateSafePipe } from '../../shared/pipes/translation.pipe';
import { formatTimeAgoTranslation, getTimeRangeString} from '../../shared/utils/date.util';
import {
  getActiveAlarmsSorted,
  getAlarmIconCode,
  getAlarmTypeColor,
} from '../../shared/utils/map/map-marker.util';
import { AlarmDto } from '../../types/dtos/alarm.dto';
import { DeviceDto } from '../../types/dtos/device.dto';
import { DeviceProfileDto } from '../../types/dtos/deviceProfile.dto';
import { DeviceMapDto } from '../../types/dtos/map.dto';
import { AlarmSeverity, AlarmStatus } from '../../types/enums/alarm.enum';
import {
  InputSelectButtonInterface,
  InputSelectInterface,
  InputTextInterface,
} from '../../types/interfaces/input.interface';
import { DashboardBoxComponent } from '../dashboard/dashboard-box/dashboard-box.component';
import { DialogDeviceDetail } from '../devices/dialog-device-detail/dialog-device-detail';
import {TranslateService} from '@ngx-translate/core';
import {LogService} from '../../services/log.service';
import {LoggingActionEnum, LoggingTargetEnum} from '../../types/enums/logging.enum';

export type MapLayerType = 'default' | 'satellite';

@Component({
  selector: 'app-map',
  imports: [
    DialogBase,
    DialogDeviceDetail,
    DashboardBoxComponent,
    FormsModule,
    InputSelectComponent,
    InputSelectButton,
    TextLabel,
    InputTextComponent,
    TranslateSafePipe,
  ],
  templateUrl: './map.component.html',
  styleUrl: './map.component.css',
})

export class MapComponent extends AbstractMap {
  @ViewChild('fullMapContainer', { static: true })
  public fullMapContainer!: ElementRef<HTMLDivElement>;

  @ViewChild('showDeviceDetails', { static: true })
  public showDeviceDetails!: DialogDeviceDetail;

  public deviceProfile: DeviceProfileDto[] = [];
  public isDeviceDetailOpen: boolean = false;
  public selectedDevice: DeviceDto | null = null;
  public selectedLayer: MapLayerType = 'default';

  public mapForm: FormGroup = new FormGroup({
    mapLayout: new FormControl('standard'),
    deviceStatus: new FormControl(),
    deviceModel: new FormControl(),
    search: new FormControl(),
  });

  public searchDeviceSelect: InputTextInterface = {
    formGroup: this.mapForm,
    id: 'search',
    label: 'common.input.search-device.label',
    isTranslated: true,
    type: 'text',
    iconLeft: 'search',
    showClear: true,
    onInput: () => {
      this.applyFilters();
    },
  };

  public deviceStatusSelect: InputSelectInterface = {
    formGroup: this.mapForm,
    isTranslated: true,
    label: 'common.input.device-status.label',
    id: 'deviceStatus',
    showClear: true,
    onInput: () => {
      this.applyFilters();
    },
    options: [
      {
        label: 'common.enum.status.online',
        isTranslated: true,
        value: 'online',
      },
      {
        label: 'common.enum.status.offline',
        isTranslated: true,
        value: 'offline',
      },
      {
        label: 'common.enum.alarm-severity.warning',
        isTranslated: true,
        value: AlarmSeverity.WARNING,
      },
      {
        label: 'common.enum.alarm-severity.info',
        isTranslated: true,
        value: AlarmSeverity.INFO,
      },
      {
        label: 'common.enum.alarm-severity.high',
        isTranslated: true,
        value: AlarmSeverity.HIGH,
      },
      {
        label: 'common.enum.alarm-severity.critical',
        isTranslated: true,
        value: AlarmSeverity.CRITICAL,
      },
    ],
  };

  public deviceModelSelect: InputSelectInterface = {
    formGroup: this.mapForm,
    isTranslated: true,
    label: 'common.input.model.label',
    id: 'deviceModel',
    showClear: true,
    onInput: () => {
      this.applyFilters();
    },
    options: [],
  };

  public mapLayout: InputSelectButtonInterface = {
    formGroup: this.mapForm,
    id: 'mapLayout',
    onInput: () => {
      this.updateMapLayer();
    },
    options: [
      {
        label: '',
        value: 'standard',
        iconLeft: 'map',
      },
      {
        label: '',
        value: 'satellite',
        iconLeft: 'satellite_alt',
      },
    ],
  };

  private _deviceCount: number = 0;
  private _destroyRef: DestroyRef = inject(DestroyRef);
  private _defaultLayer!: L.TileLayer;
  private _satelliteLayer!: L.TileLayer;
  private _activeBaseLayer!: L.TileLayer;

  constructor(
    override readonly _deviceService: DeviceService,
    override readonly _mapIconService: MapIconService,
    private readonly _deviceProfileService: DeviceProfileService,
    private readonly _pageLoaderService: PageLoaderService,
    private readonly _router: Router,
    private readonly _translateService: TranslateService,
    private readonly _logService: LogService,
  ) {
    super(_deviceService, _mapIconService);
  }

  public ngOnInit(): void {
    this._deviceService.devices$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((devices: DeviceDto[]) => {
        this.updateDevicesToMapDevices(devices);
        this.devices =  [...this.mapDevices];
        this._deviceCount = this.mapDevices.length;
        this.applyFilters();
      });

    this._deviceProfileService.fetchDeviceProfiles();

    this._deviceProfileService.devicesProfile$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((profiles: DeviceProfileDto[]) => {
        this.deviceModelSelect.options = [
          {
            label: 'Ohne Model',
            value: '-1',
          },
          ...profiles
            .filter((profile: DeviceProfileDto) => !!profile.model)
            .map((profile: DeviceProfileDto) => ({
              label: profile.model ?? '',
              value: profile.id.toString(),
            })),
        ];
      });
  }

  public async ngAfterViewInit(): Promise<void> {
    await this._pageLoaderService.waitLoadingFinish();
    this._map = L.map(this.fullMapContainer.nativeElement, {
      center: this._deviceService.getCenter(),
      zoom: 14,
    });

    this._defaultLayer = this.createDefaultTileLayer();

    this._satelliteLayer = L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      {
        minZoom: 0,
        maxZoom: 20,
        attribution: '&copy; CNES, Distribution Airbus DS, © Airbus DS, © PlanetObserver (Contains Copernicus Data) | &copy; <a href="https://www.stadiamaps.com/" target="_blank">Stadia Maps</a> &copy; <a href="https://openmaptiles.org/" target="_blank">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      },
    );

    this.selectedLayer = 'default';
    this._activeBaseLayer = this._defaultLayer;
    this._activeBaseLayer.addTo(this._map);

    this.markerLayer.addTo(this._map);

    this.resizeObserver = new ResizeObserver(() => {
      this._map.invalidateSize();
    });

    this._map.on('zoomend', () => {
      this.addMarkers();
    });

    this.resizeObserver.observe(this.fullMapContainer.nativeElement);

    this.addMarkers();
    this._fitMapToDevices();
  }

  public getDeviceCount(): number {
    return this.mapDevices.length
  }
  public getDeviceTotal(): number {
    return this._deviceCount;
  }

  public updateMapLayer(): void {
    if (!this._map) return;

    this._map.removeLayer(this._activeBaseLayer);

    const {
            mapLayout,
          } = this.mapForm.value;

    this._activeBaseLayer = mapLayout === 'satellite' ? this._satelliteLayer : this._defaultLayer;

    this._map.addLayer(this._activeBaseLayer);
  }

  public readonly closeShowDetails = (): void => {
    this.isDeviceDetailOpen = false;
  };

  public openShowDetails(device: DeviceDto): void {

    this.selectedDevice = device;
    this.isDeviceDetailOpen = true;
  }

  override updateMarker(marker: Marker, device: DeviceMapDto) {
    marker.bindPopup(`
<div class="device-popup-content">
  <div class="left-side">
    <p class="serial-number">
      ${this._translateService.instant('common.fields.device')}:
    </p>

    <p class="status">
      ${this._translateService.instant('common.fields.status')}:
    </p>

    ${ this._getActiveAlarm(device, true) }

    <p class="index">
      Index:
    </p>
  </div>

  <div class="right-side">
    <p class="serial-number">
      ${ device.serial_number ?? '-' }
    </p>

    <p class="status ${ this._deviceService.getOnlineStatus(device) }">
      <span class="status-icon"></span>

      ${ formatTimeAgoTranslation(this._translateService,device.last_measurement_received_time!) }
    </p>

    ${ this._getActiveAlarm(device, false) }

    <p class="index">
      ${ this.getDeviceValue(device) }
    </p>
  </div>
</div>

<div class="button-container">
  <button type="button" class="button-show-details">
    ${ this._translateService.instant('common.action.show-more') }
  </button>
</div>
    `);

    marker.on('popupopen', () => {
      const popupElement: HTMLElement | undefined = marker.getPopup()
        ?.getElement();

      this._logService.sendLog({
        target: LoggingTargetEnum.DEVICE_MAP,
        action: LoggingActionEnum.VIEW,
        target_id: device.id,
      });

      const detailsButton: Element | null | undefined = popupElement?.querySelector('.button-show-details');

      if (!detailsButton) {
        return;
      }

      detailsButton.addEventListener('click', () => {
        this.openShowDetails(device);
      });

      this._bindAlarmIconClicks(popupElement!);
    });
  }

  private _getActiveAlarmIcons(device: DeviceDto): string {
    const activeAlarms = getActiveAlarmsSorted(device);

    if (activeAlarms.length === 0) {
      return '';
    }

    const iconSize: number = this.getMarkerIconSize();
    const maxIcons = 4;

    const visibleAlarms: AlarmDto[]  = activeAlarms.slice(0, maxIcons);
    const hasMore: boolean = activeAlarms.length > maxIcons;

    const iconsHtml: string = visibleAlarms
      .map((alarm) => {
        const color: string = getAlarmTypeColor(alarm.alarm_type);
        const iconCode: string = getAlarmIconCode(alarm);

        return `
        <button
          type="button"
          class="alarm-icon"
          data-alarm-id="${ alarm.id }">
          ${ this._mapIconService.renderIcon(color, iconCode, iconSize) }
        </button>
      `;
      })
      .join('');

    const ellipsisHtml = hasMore
                         ? `<span class="alarm-icon alarm-icon-more">...</span>`
                         : '';

    return `${ iconsHtml }${ ellipsisHtml }`;
  }

  private _getActiveAlarm(device: DeviceDto, isLeftSide: boolean): string {
    const activeAlarms = device.alarms?.filter((alarm) => alarm.status !== AlarmStatus.RESOLVED);

    if (!activeAlarms || activeAlarms.length === 0) {
      return '';
    }

    if (isLeftSide) {
      return `
      <p class="alarm">
        ${this._translateService.instant('common.fields.alarms')}:
      </p>
      `;
    } else {
      return `
      <div class="alarm">
        ${ this._getActiveAlarmIcons(device) }
      </div>
      `;
    }
  }

  private _bindAlarmIconClicks(container: HTMLElement): void {
    const alarmButtons = container.querySelectorAll('[data-alarm-id]');

    alarmButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();

        const alarmId = (event.currentTarget as HTMLElement).getAttribute('data-alarm-id');
        if (!alarmId) return;

        this._router.navigate(['/alarms'], {
          queryParams: { id: alarmId },
        });
      });
    });
  }

  private applyFilters(): void {
    let filteredDevices: DeviceMapDto[] = [...this.devices];

    filteredDevices = this._filterDevicesByStatus(filteredDevices);
    filteredDevices = this._filterDevicesByModel(filteredDevices);
    filteredDevices = this._filterDevicesBySearch(filteredDevices);

    this.mapDevices = filteredDevices;
    this.addMarkers();
  }

  private _filterDevicesByStatus(devices: DeviceMapDto[]): DeviceMapDto[] {
    const selectedStatus: string = String(this.mapForm.get('deviceStatus')?.value ?? '')
      .trim()
      .toLowerCase();

    if (!selectedStatus) {
      return devices;
    }

    return devices.filter((device: DeviceMapDto) =>
      this.deviceMatchesStatusFilter(device, selectedStatus),
    );
  }

  private _filterDevicesBySearch(devices: DeviceMapDto[]): DeviceMapDto[] {
    const searchText: string = String(this.mapForm.get('search')?.value ?? '');

    return devices.filter((device: DeviceMapDto) => {
      const deviceString: string = `${ device.id } ${ device.device_eui } ${ device.status } ${ device.position?.address } ${ device.serial_number }`;
      return deviceString.toLowerCase()
        .includes(searchText.toLowerCase());
    });
  }

  private deviceMatchesStatusFilter(
    device: DeviceDto,
    selectedStatus: string,
  ): boolean {
    const deviceStatus: string = String(device.status ?? '')
      .trim()
      .toLowerCase();

    if (
      selectedStatus === AlarmSeverity.CRITICAL
      || selectedStatus === AlarmSeverity.WARNING
      || selectedStatus === AlarmSeverity.INFO
    ) {
      for (const alarm of device.alarms ?? []) {
        if (this.isActiveAlarm(alarm) && alarm.severity === selectedStatus) {
          return true;
        }
      }
    }

    return deviceStatus === selectedStatus;
  }

  private isActiveAlarm(alarm: AlarmDto): boolean {
    return alarm.status !== AlarmStatus.RESOLVED
  }

  private _filterDevicesByModel(devices: DeviceMapDto[]): DeviceMapDto[] {
    let modelSelect = this.mapForm.get('deviceModel')?.value ?? '';
    modelSelect = Number(modelSelect);

    if (!modelSelect) {return devices}

    return devices.filter((device: DeviceMapDto) =>
      !modelSelect || (device.device_profile_id ?? -1) === modelSelect
    );
  }

  protected readonly getTimeRangeString = getTimeRangeString;
}
